// Copyright 2025-2026 - Hewlett Packard Enterprise Company
import React, { createContext, useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { postQuery, getChatAsync } from '../actions/Api';

// Create the ChatContext
export const ChatContext = createContext();

// ChatProvider component
export function ChatProvider({ children }) {
  const [conversations, setConversations] = useState([]);
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);

  const createLoadingConversation = useCallback(
    query => ({
      query,
      answer: 'Loading',
      sessionId: null,
      showFollowUpQuestions: true,
      showFeedback: true,
    }),
    [],
  );

  const addConversation = useCallback(
    query => {
      setConversations(prevConversations => [
        ...prevConversations,
        createLoadingConversation(query),
      ]);
    },
    [createLoadingConversation],
  );

  const updateConversation = useCallback((index, answer, sessionId = null) => {
    setConversations(prevConversations => {
      const updatedConversations = [...prevConversations];
      if (index >= 0 && index < updatedConversations.length) {
        updatedConversations[index] = {
          ...updatedConversations[index],
          answer,
          sessionId: answer?.sessionId || sessionId || null,
          showFollowUpQuestions: answer?.followupQuestions?.length > 0,
          showFeedback: true,
        };
        return updatedConversations;
      }
      return prevConversations;
    });
  }, []);

  const clearConversations = useCallback(() => {
    setConversations([]);
  }, []);

  const hideAllFollowUpQuestions = useCallback(() => {
    setConversations(prevConversations =>
      prevConversations.map(conv => ({
        ...conv,
        showFollowUpQuestions: false,
      })),
    );
  }, []);

  const hideAllFeedback = useCallback(() => {
    setConversations(prevConversations =>
      prevConversations.map(conv => ({
        ...conv,
        showFeedback: false,
      })),
    );
  }, []);

  const getAsyncResponse = useCallback(async id => {
    const maxRetries = 9;
    const pollingInterval = 5000; // 5 seconds

    let answer = null;

    for (let i = 0; i < maxRetries; i++) {
      // eslint-disable-next-line no-await-in-loop, no-promise-executor-return
      await new Promise(r => setTimeout(r, pollingInterval));

      try {
        // eslint-disable-next-line no-await-in-loop
        const chatResponse = await getChatAsync(id);

        if (
          chatResponse?.data &&
          chatResponse.data.summary !==
            'Chat response is being processed asynchronously.'
        ) {
          answer = chatResponse?.data;
          break;
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error during polling:', error);
        break; // Exit the loop on error
      }
    }

    if (!answer) {
      throw new Error('Polling timed out');
    }

    return answer;
  }, []);

  const handleQueryClick = useCallback(
    async query => {
      const index = conversations.length;
      const sessionId = conversations[conversations.length - 1]?.sessionId;
      let id = null;
      addConversation(query);
      setIsFeedbackOpen(false);
      try {
        const response = await postQuery(query, sessionId);
        id = response?.data?.id;
        const answer = await getAsyncResponse(id);

        updateConversation(index, answer);
      } catch (error) {
        updateConversation(
          index,
          { summary: 'Error retrieving answer', id },
          sessionId,
        );
      }
    },
    [conversations, addConversation, updateConversation, getAsyncResponse],
  );

  const handleFreshQueryClick = useCallback(
    async query => {
      let id = null;
      setConversations([createLoadingConversation(query)]);
      setIsFeedbackOpen(false);
      try {
        const response = await postQuery(query, null);
        id = response?.data?.id;
        const answer = await getAsyncResponse(id);

        updateConversation(0, answer, null);
      } catch (error) {
        updateConversation(0, { summary: 'Error retrieving answer', id }, null);
      }
    },
    [createLoadingConversation, getAsyncResponse, updateConversation],
  );

  // Use useMemo to memoize the context value
  const contextValue = useMemo(
    () => ({
      conversations,
      addConversation,
      updateConversation,
      clearConversations,
      handleQueryClick,
      handleFreshQueryClick,
      hideAllFollowUpQuestions,
      getAsyncResponse,
      isFeedbackOpen,
      setIsFeedbackOpen,
      hideAllFeedback,
    }),
    [
      conversations,
      addConversation,
      updateConversation,
      clearConversations,
      handleQueryClick,
      handleFreshQueryClick,
      hideAllFollowUpQuestions,
      getAsyncResponse,
      isFeedbackOpen,
      setIsFeedbackOpen,
      hideAllFeedback,
    ],
  );

  return (
    <ChatContext.Provider value={contextValue}>{children}</ChatContext.Provider>
  );
}

ChatProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
